import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { getSocket } from '../services/socket';

// The user's connected MT5 accounts, plus which one the dashboard is currently
// scoped to.
//
// `selectedId` of null means "All accounts" — the portfolio view. Every
// dashboard read passes `scopeParams` straight into its request params, so a
// view never has to know whether one account or all of them are in play.
//
// The selection is persisted per user in localStorage: switching accounts is a
// navigational act, and losing it on every reload would be maddening for
// someone running a live and a demo account side by side.
const AccountContext = createContext({
  accounts: [], selectedId: null, selected: null, loading: true,
  select: () => {}, refresh: async () => {}, scopeParams: {},
  limit: 0, used: 0, canConnectMore: false,
});

const storageKey = (userId) => `mt5AccountSelection:${userId}`;

export function AccountProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || user?._id || null;

  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState({ limit: 0, used: 0, canConnectMore: false });
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!userId || user?.role === 'admin') { setAccounts([]); setLoading(false); return []; }
    try {
      const { data } = await api.get('/accounts');
      const list = Array.isArray(data?.accounts) ? data.accounts : [];
      setAccounts(list);
      setMeta({
        limit: data?.limit || 0,
        used: data?.used || 0,
        canConnectMore: !!data?.canConnectMore,
      });
      return list;
    } catch (_) {
      // Leave the previous list in place on a transient error — blanking the
      // switcher mid-session would look like the accounts vanished.
      return accounts;
    } finally {
      setLoading(false);
    }
  }, [userId, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  // Restore the saved selection once the list is known, dropping it if that
  // account is gone (deleted, or a different user logged in on this browser).
  useEffect(() => {
    if (!userId || loading) return;
    const saved = localStorage.getItem(storageKey(userId));
    if (saved && accounts.some(a => a.id === saved)) setSelectedId(saved);
    else if (saved) localStorage.removeItem(storageKey(userId));
  }, [userId, loading, accounts]);

  const select = useCallback((id) => {
    setSelectedId(id || null);
    if (!userId) return;
    if (id) localStorage.setItem(storageKey(userId), id);
    else localStorage.removeItem(storageKey(userId));
  }, [userId]);

  // Container status changes arrive per account; refresh so the switcher's
  // status dots and the Accounts page stay live without polling.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;
    const onStatus = () => refresh();
    socket.on('container:status', onStatus);
    return () => { socket.off('container:status', onStatus); };
  }, [refresh]);

  const selected = useMemo(
    () => accounts.find(a => a.id === selectedId) || null,
    [accounts, selectedId],
  );

  // Spread into an axios `params` object. Empty when viewing all accounts, so
  // the backend applies its own "every account of this user" default.
  const scopeParams = useMemo(
    () => (selectedId ? { accountId: selectedId } : {}),
    [selectedId],
  );

  const value = useMemo(() => ({
    accounts, selectedId, selected, loading, select, refresh, scopeParams,
    ...meta,
  }), [accounts, selectedId, selected, loading, select, refresh, scopeParams, meta]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccounts() {
  return useContext(AccountContext);
}
